/**
 * Admin Compliance Runs Page
 *
 * Global search and view for all compliance runs across orgs.
 */

import { useState } from 'react';
import { AdminCard } from '@/components/admin/AdminShell';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useToast } from '@/hooks/use-toast';
import {
  Search,
  MoreHorizontal,
  Eye,
  RefreshCw,
  Download,
  CheckCircle,
  XCircle,
  Clock,
  AlertTriangle,
  Building2,
  Play,
} from 'lucide-react';
import { ComplianceRunSummary } from '@/integrations/firebase/types';
import { format } from 'date-fns';

// Compliance runs - will be loaded from Firestore
// TODO: Replace with real Firestore query for compliance runs
const complianceRuns: (ComplianceRunSummary & { orgName: string })[] = [];

export default function AdminRuns() {
  const { toast } = useToast();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [triggerFilter, setTriggerFilter] = useState<string>('all');

  // Filter runs
  const filteredRuns = complianceRuns.filter((run) => {
    const matchesSearch =
      run.jobTitle.toLowerCase().includes(searchTerm.toLowerCase()) ||
      run.orgName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      run.id.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || run.status === statusFilter;
    const matchesTrigger =
      triggerFilter === 'all' || run.triggeredBy === triggerFilter;
    return matchesSearch && matchesStatus && matchesTrigger;
  });

  const getStatusBadge = (status: ComplianceRunSummary['status']) => {
    switch (status) {
      case 'completed':
        return (
          <Badge className="bg-green-500/20 text-green-400 border-green-500/40">
            <CheckCircle className="h-3 w-3 mr-1" />
            Completed
          </Badge>
        );
      case 'failed':
        return (
          <Badge className="bg-red-500/20 text-red-400 border-red-500/40">
            <XCircle className="h-3 w-3 mr-1" />
            Failed
          </Badge>
        );
      case 'running':
        return (
          <Badge className="bg-blue-500/20 text-blue-400 border-blue-500/40">
            <Clock className="h-3 w-3 mr-1 animate-pulse" />
            Running
          </Badge>
        );
      case 'queued':
        return (
          <Badge className="bg-gray-500/20 text-gray-400 border-gray-500/40">
            <Clock className="h-3 w-3 mr-1" />
            Queued
          </Badge>
        );
      default:
        return null;
    }
  };

  const handleViewRun = (runId: string) => {
    toast({
      title: 'Not Implemented',
      description: 'Run detail view will open in a modal',
    });
  };

  const handleRerunCheck = (runId: string) => {
    toast({
      title: 'Not Implemented',
      description: 'Re-run will be wired to backend',
    });
  };

  const handleDownloadBundle = (runId: string) => {
    toast({
      title: 'Not Implemented',
      description: 'Debug bundle download will be wired to backend',
    });
  };

  // Stats
  const totalRuns = complianceRuns.length;
  const runningRuns = complianceRuns.filter((r) => r.status === 'running').length;
  const failedRuns = complianceRuns.filter((r) => r.status === 'failed').length;
  const adminTriggered = complianceRuns.filter((r) => r.triggeredBy === 'admin').length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-semibold text-white">Compliance Runs</h1>
        <p className="text-gray-400 mt-1">
          Search and manage compliance runs across all organizations
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-4">
        <AdminCard>
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-[hsl(220,70%,50%)]/20 flex items-center justify-center">
              <Play className="h-5 w-5 text-[hsl(220,70%,50%)]" />
            </div>
            <div>
              <p className="text-2xl font-semibold text-white">{totalRuns}</p>
              <p className="text-sm text-gray-400">Total Runs</p>
            </div>
          </div>
        </AdminCard>

        <AdminCard>
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
              <Clock className="h-5 w-5 text-blue-400" />
            </div>
            <div>
              <p className="text-2xl font-semibold text-white">{runningRuns}</p>
              <p className="text-sm text-gray-400">Running Now</p>
            </div>
          </div>
        </AdminCard>

        <AdminCard>
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-red-500/20 flex items-center justify-center">
              <XCircle className="h-5 w-5 text-red-400" />
            </div>
            <div>
              <p className="text-2xl font-semibold text-white">{failedRuns}</p>
              <p className="text-sm text-gray-400">Failed</p>
            </div>
          </div>
        </AdminCard>

        <AdminCard>
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-amber-500/20 flex items-center justify-center">
              <Building2 className="h-5 w-5 text-amber-400" />
            </div>
            <div>
              <p className="text-2xl font-semibold text-white">{adminTriggered}</p>
              <p className="text-sm text-gray-400">Admin Triggered</p>
            </div>
          </div>
        </AdminCard>
      </div>

      {/* Filters */}
      <AdminCard>
        <div className="flex items-center gap-4">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
            <Input
              placeholder="Search by job, org, or run ID..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 bg-[hsl(220,20%,15%)] border-[hsl(220,15%,25%)] text-white placeholder:text-gray-500"
            />
          </div>

          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-40 bg-[hsl(220,20%,15%)] border-[hsl(220,15%,25%)] text-white">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent className="bg-[hsl(220,22%,12%)] border-[hsl(220,15%,25%)]">
              <SelectItem value="all">All Statuses</SelectItem>
              <SelectItem value="completed">Completed</SelectItem>
              <SelectItem value="failed">Failed</SelectItem>
              <SelectItem value="running">Running</SelectItem>
              <SelectItem value="queued">Queued</SelectItem>
            </SelectContent>
          </Select>

          <Select value={triggerFilter} onValueChange={setTriggerFilter}>
            <SelectTrigger className="w-40 bg-[hsl(220,20%,15%)] border-[hsl(220,15%,25%)] text-white">
              <SelectValue placeholder="Triggered By" />
            </SelectTrigger>
            <SelectContent className="bg-[hsl(220,22%,12%)] border-[hsl(220,15%,25%)]">
              <SelectItem value="all">All Sources</SelectItem>
              <SelectItem value="user">User</SelectItem>
              <SelectItem value="admin">Admin</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </AdminCard>

      {/* Runs Table */}
      <AdminCard className="p-0">
        <Table>
          <TableHeader>
            <TableRow className="border-[hsl(220,15%,20%)] hover:bg-transparent">
              <TableHead className="text-gray-400">Organization</TableHead>
              <TableHead className="text-gray-400">Job</TableHead>
              <TableHead className="text-gray-400">Status</TableHead>
              <TableHead className="text-gray-400">Results</TableHead>
              <TableHead className="text-gray-400">Started</TableHead>
              <TableHead className="text-gray-400">Source</TableHead>
              <TableHead className="text-gray-400 w-[50px]"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredRuns.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={7}
                  className="text-center text-gray-500 py-8"
                >
                  No compliance runs found
                </TableCell>
              </TableRow>
            ) : (
              filteredRuns.map((run) => (
                <TableRow
                  key={run.id}
                  className="border-[hsl(220,15%,20%)] hover:bg-[hsl(220,15%,12%)]"
                >
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Building2 className="h-4 w-4 text-gray-500" />
                      <span className="text-white">{run.orgName}</span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div>
                      <p className="text-white font-medium">{run.jobTitle}</p>
                      <p className="text-xs text-gray-500">{run.id}</p>
                    </div>
                  </TableCell>
                  <TableCell>{getStatusBadge(run.status)}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2 text-sm">
                      <span className="text-green-400">{run.passedRules} ✓</span>
                      <span className="text-red-400">{run.failedRules} ✗</span>
                      {run.warningRules > 0 && (
                        <span className="text-amber-400">
                          {run.warningRules}{' '}
                          <AlertTriangle className="inline h-3 w-3" />
                        </span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="text-gray-400">
                    {run.startedAt
                      ? format(run.startedAt.toDate(), 'MMM d, h:mm a')
                      : '-'}
                  </TableCell>
                  <TableCell>
                    <Badge
                      variant="outline"
                      className={`text-xs ${
                        run.triggeredBy === 'admin'
                          ? 'border-amber-500/40 text-amber-400'
                          : 'border-gray-600 text-gray-400'
                      }`}
                    >
                      {run.triggeredBy === 'admin' ? 'Admin' : 'User'}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-gray-400 hover:text-white hover:bg-[hsl(220,15%,20%)]"
                        >
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent
                        align="end"
                        className="bg-[hsl(220,22%,12%)] border-[hsl(220,15%,20%)]"
                      >
                        <DropdownMenuItem
                          onClick={() => handleViewRun(run.id)}
                          className="text-gray-300 hover:text-white hover:bg-[hsl(220,15%,20%)] cursor-pointer"
                        >
                          <Eye className="h-4 w-4 mr-2" />
                          View Details
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => handleRerunCheck(run.id)}
                          className="text-gray-300 hover:text-white hover:bg-[hsl(220,15%,20%)] cursor-pointer"
                        >
                          <RefreshCw className="h-4 w-4 mr-2" />
                          Re-run Check
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => handleDownloadBundle(run.id)}
                          className="text-gray-300 hover:text-white hover:bg-[hsl(220,15%,20%)] cursor-pointer"
                        >
                          <Download className="h-4 w-4 mr-2" />
                          Download Debug Bundle
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </AdminCard>
    </div>
  );
}
